import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-100 flex items-start justify-center p-4 pt-12">
    <div className="bg-white rounded-lg shadow-lg border-t-4 border-green-600 w-full max-w-md p-6" data-testid="approve-suggestion-card">
      <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Safety Onsite Solutions</div>
      {children}
    </div>
  </div>
);

export default function ApproveSuggestion() {
  const { token } = useParams();
  const [sug, setSug] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [welcome, setWelcome] = useState(null); // null | "sending" | "sent" | "skipped"

  useEffect(() => {
    api.get(`/public/supervisor-suggestions/${token}`)
      .then((r) => setSug(r.data))
      .catch((e) => setErr(e.response?.data?.detail?.toString() || "This link is not valid."));
  }, [token]);

  const add = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/public/supervisor-suggestions/${token}/add`);
      setDone(r.data);
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusy(false); }
  };

  const sendWelcome = async () => {
    setWelcome("sending"); setErr("");
    try {
      await api.post(`/public/supervisor-suggestions/${token}/send-welcome`);
      setWelcome("sent");
    } catch (e) {
      setWelcome(null);
      setErr(e.response?.data?.detail?.toString() || e.message);
    }
  };


  if (err && !sug) return <Shell><div data-testid="suggestion-error" className="text-rose-600 font-semibold">{err}</div></Shell>;
  if (!sug) return <Shell><div className="text-slate-500">Loading…</div></Shell>;

  if (done) {
    return (
      <Shell>
        <div data-testid="suggestion-added-note">
          <h1 className="text-xl font-black text-emerald-700">✓ {done.already_present ? "Already added" : "Supervisor added"}</h1>
          <p className="mt-2 text-sm text-slate-700">
            <b>{done.supervisor_name}</b> is now an approving supervisor on <b>{done.project_name}</b>.
            {done.worker_name ? <> {done.worker_name} has been notified that they can submit their timesheet.</> : null}
          </p>
          {welcome === "sent" ? (
            <div className="mt-4 text-sm font-bold text-emerald-700" data-testid="welcome-sent-note">
              ✓ Welcome email sent to {done.supervisor_name}.
            </div>
          ) : welcome === "skipped" ? (
            <div className="mt-4 text-sm text-slate-500" data-testid="welcome-skipped-note">No welcome email sent.</div>
          ) : (
            <div className="mt-4 border-t border-slate-100 pt-4" data-testid="welcome-prompt-public">
              {err && <div className="mb-2 text-sm text-rose-600 font-semibold">{err}</div>}
              <p className="text-sm text-slate-600">
                {done.supervisor_is_new
                  ? "They haven't supervised for us before. Send them the welcome email explaining how electronic timesheet approvals work?"
                  : "They've supervised for us before, so they already know how approvals work. Send the welcome email anyway?"}
              </p>
              <div className="mt-3 flex gap-2">
                <button data-testid="welcome-public-send-btn" disabled={welcome === "sending"} onClick={sendWelcome}
                  className={`flex-1 h-11 rounded-md font-bold text-white disabled:opacity-50 ${done.supervisor_is_new ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-600 hover:bg-slate-700"}`}>
                  {welcome === "sending" ? "Sending…" : "Send welcome email"}
                </button>
                <button data-testid="welcome-public-skip-btn" disabled={welcome === "sending"} onClick={() => setWelcome("skipped")}
                  className="flex-1 h-11 border border-slate-300 rounded-md font-bold text-slate-600 hover:bg-slate-50">
                  {done.supervisor_is_new ? "Skip" : "No need"}
                </button>
              </div>
            </div>
          )}
        </div>
      </Shell>
    );
  }

  if (sug.status === "added") {
    return <Shell><div data-testid="suggestion-already-added" className="text-emerald-700 font-semibold">✓ This approver was already added to {sug.project_name}. Nothing else to do.</div></Shell>;
  }
  if (sug.status === "dismissed") {
    return <Shell><div className="text-slate-600 font-semibold">This suggestion was dismissed by the office.</div></Shell>;
  }
  if (sug.status === "expired") {
    return <Shell><div className="text-amber-700 font-semibold">This link has expired. Add the supervisor from the Projects tab instead.</div></Shell>;
  }

  return (
    <Shell>
      <h1 className="text-xl font-black text-slate-900">Add approving supervisor?</h1>
      <p className="mt-2 text-sm text-slate-600">
        <b>{sug.worker_name}</b> couldn't submit a timesheet because <b>{sug.project_name}</b> has no
        approving supervisor. They sent these details:
      </p>
      <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
        <div><span className="text-slate-500">Name:</span> <b>{sug.name}</b></div>
        <div><span className="text-slate-500">Email:</span> <b>{sug.email}</b></div>
        <div><span className="text-slate-500">Cell:</span> <b>{sug.phone || "—"}</b></div>
      </div>
      {err && <div className="mt-3 text-sm text-rose-600 font-semibold" data-testid="suggestion-error">{err}</div>}
      <button data-testid="suggestion-public-add-btn" disabled={busy} onClick={add}
        className="mt-4 w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md disabled:opacity-50">
        {busy ? "Adding…" : `Add ${sug.name} to ${sug.project_name}`}
      </button>
      <p className="mt-2 text-xs text-slate-400">This adds them as the approving supervisor and notifies {sug.worker_name} that they can submit their timesheet.</p>
    </Shell>
  );
}
